import { useMemo, useState, type JSX } from "react";
import {
  useCreateAllowedDirPrefixForOrganization,
  useDeleteAllowedDirPrefixForOrganization,
  useGetAllowedDirPrefixesForOrganization,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading";
import {
  DirectoryKind,
  GetUserOrganizationDetailsForAdminResponseBodyItem,
} from "common";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";
import { isValidDirPrefix } from "@via/utils/form-validators.tsx";

const DIRECTORY_KIND_LABELS: Record<DirectoryKind, string> = {
  [DirectoryKind.Work]: "Work",
  [DirectoryKind.Download]: "Download",
  [DirectoryKind.Publish]: "Publish",
};

const DIRECTORY_KIND_ORDER: DirectoryKind[] = [
  DirectoryKind.Work,
  DirectoryKind.Download,
  DirectoryKind.Publish,
];

export default function StoragePanel({
  organization,
}: {
  organization: GetUserOrganizationDetailsForAdminResponseBodyItem;
}): JSX.Element {
  const organizationId = organization?.id ?? "";

  const { data: allowed = [], isLoading } =
    useGetAllowedDirPrefixesForOrganization(
      { organizationId },
      !!organizationId
    );

  const { mutateAsync: createAllowed, isPending: isCreating } =
    useCreateAllowedDirPrefixForOrganization();
  const { mutateAsync: deleteAllowed, isPending: isDeleting } =
    useDeleteAllowedDirPrefixForOrganization();

  const [directoryKind, setDirectoryKind] = useState<DirectoryKind>(
    DirectoryKind.Work
  );
  const [pathPrefix, setPathPrefix] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const trimmed = pathPrefix.trim();
  const isShapeValid = trimmed.length === 0 || isValidDirPrefix(trimmed);

  const canAdd = useMemo(() => {
    return !!organizationId && isValidDirPrefix(trimmed) && !isCreating;
  }, [trimmed, isCreating, organizationId]);

  const onAdd = async () => {
    if (!canAdd) return;
    await createAllowed({
      organizationId,
      directoryKind,
      pathPrefix: trimmed,
    });
    setPathPrefix("");
  };

  const onDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteAllowed({ organizationId, allowedDirPrefixId: id });
    } finally {
      setDeletingId(null);
    }
  };

  const groupedByKind = useMemo(() => {
    const byKind = new Map<DirectoryKind, typeof allowed>();
    for (const kind of DIRECTORY_KIND_ORDER) byKind.set(kind, []);
    for (const row of allowed) {
      byKind.get(row.directoryKind)?.push(row);
    }
    return DIRECTORY_KIND_ORDER.map(kind => ({
      kind,
      rows: byKind.get(kind) ?? [],
    })).filter(group => group.rows.length > 0);
  }, [allowed]);

  return (
    <div data-testid="storage-panel" className="space-y-6">
      <div className="text-sm text-gray-700">
        Configure which run-directory path prefixes organization members can use
        for work, download, and publish directories.
      </div>

      <div className="flex items-end gap-2">
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Directory kind
          </label>
          <select
            data-testid="dir-prefix-kind-select"
            value={directoryKind}
            onChange={e => setDirectoryKind(e.target.value as DirectoryKind)}
            className="mt-2 block w-32 rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
            {DIRECTORY_KIND_ORDER.map(kind => (
              <option key={kind} value={kind}>
                {DIRECTORY_KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-900">
            Path prefix
          </label>
          <input
            type="text"
            data-testid="dir-prefix-input"
            value={pathPrefix}
            onChange={e => setPathPrefix(e.target.value)}
            placeholder="s3://bucket/path or /mnt/data"
            className="mt-2 block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
          />
          {!isShapeValid ? (
            <p className="mt-1 text-xs text-rose-500">
              Enter an absolute path, or a prefix starting with s3:// or gs://.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void onAdd()}
          disabled={!canAdd}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          <Spinner loading={isCreating}>Add</Spinner>
        </button>
      </div>

      <div className="rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          Approved directory prefixes
        </div>
        {isLoading ? (
          <div className="p-4">
            <SkeletonLoader count={1} />
          </div>
        ) : allowed.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-6 text-sm text-gray-600">
            <EmptyDataIcon />
            <div>
              No approved directory prefixes configured for this organization.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groupedByKind.map(group => (
              <div key={group.kind}>
                <div
                  data-testid={`dir-prefix-group-${group.kind}`}
                  className="bg-gray-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {DIRECTORY_KIND_LABELS[group.kind]}
                </div>
                <ul className="divide-y divide-gray-200">
                  {group.rows.map(row => (
                    <li
                      key={row.id}
                      className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                      <div className="min-w-0 flex-1 truncate text-gray-900">
                        {row.pathPrefix}
                      </div>
                      <button
                        type="button"
                        onClick={() => void onDelete(row.id)}
                        disabled={isDeleting && deletingId === row.id}
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
                        <Spinner loading={isDeleting && deletingId === row.id}>
                          Remove
                        </Spinner>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
